import { useMutation, useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import SsoConfigFields, { SSO_CONFIG_FORM_DEFAULT_VALUES } from './SsoConfigFields.tsx';
import Button from '../../../core/components/Button.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_SSO_PATH } from '../../constants.ts';
import { GET_SSO_CONFIGS, UPDATE_SSO_CONFIG } from '../../queries.ts';

const CustomerSsoEditPage = () => {
    const { t } = useTranslation('customer');
    const { configId } = useParams();
    const navigate = useNavigate();
    const { publishToast } = useToaster();

    const { data, refetch } = useQuery(GET_SSO_CONFIGS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const config = data?.ssoConfigs?.find((c) => c.id === configId);

    const form = useForm({ defaultValues: SSO_CONFIG_FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (config) {
            form.reset({
                ...SSO_CONFIG_FORM_DEFAULT_VALUES,
                isRequired: config.isRequired,
                providerName: config.providerName || '',
                domain: config.domain || '',
                allowSelfRegistration: config.allowSelfRegistration,
                tenantId: config.tenantId || '',
                clientId: config.clientId || '',
                clientSecret: '',
            });
        }
    }, [config]);

    const [updateSsoConfig, { loading: isSubmitting }] = useMutation(UPDATE_SSO_CONFIG);
    const handleSubmit = async (formData) => {
        try {
            const input = {
                id: config.id,
                isRequired: formData.isRequired,
                providerName: formData.providerName,
                domain: formData.domain,
                allowSelfRegistration: formData.allowSelfRegistration,
                tenantId: formData.tenantId,
                clientId: formData.clientId,
                // Leave blank to keep the existing secret; only send when the user entered a new one.
                ...(formData.clientSecret ? { clientSecret: formData.clientSecret } : {}),
            };
            await updateSsoConfig({ variables: { input } });
            await refetch();
            publishToast({ description: t('sso.form.updateSuccess'), status: 'success' });
            navigate(url(CUSTOMER_SSO_PATH));
        } catch (e) {
            console.error(e);
            publishToast({ description: t('sso.form.updateError'), status: 'error' });

            if (e.message === 'Unknown provider') {
                form.setError('providerName', { message: t('sso.form.unknownProvider') });
            }
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header>
                    <IntegrationBreadcrumb
                        backTooltip={t('sso.backTooltip')}
                        items={[
                            { label: t('sso.title'), to: url(CUSTOMER_SSO_PATH) },
                            { label: config?.providerName || t('sso.form.updateTitle') },
                        ]}
                    />
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <SettingsFormCard onSubmit={form.handleSubmit(handleSubmit)} title={t('sso.form.updateTitle')}>
                        <SsoConfigFields control={form.control} errors={formErrors} />

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={isSubmitting}
                            errorCount={countFieldErrors(form.formState.errors)}
                        >
                            <Button type="button" asChild>
                                <Link to={url(CUSTOMER_SSO_PATH)}>{t('sso.form.cancelButton')}</Link>
                            </Button>
                        </SaveStatus>
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default CustomerSsoEditPage;
